import React, { useState, useEffect, useContext, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Minus,
  RotateCcw,
  Star,
  ShieldAlert,
  MessageSquare,
  Globe,
} from "lucide-react";
import organizationsService from "../../services/organizationsService";
import AuthContext from "../../context/AuthContext";
import {
  DEFAULT_NOMINATION_PHRASE,
  DEFAULT_RECOGNITION_PHRASE,
} from "../../context/OrgThemeContext";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import RecognitionLogsCard from "./RecognitionLogsCard";
import LanguageSelect from "../LanguageSelect";
import i18nService from "../../services/i18nService";
import { useTranslation, useFormat } from "../../i18n";

// Sample names for the post-text preview. Not translated: they are names.
const PREVIEW_SENDER = "Jamie Chen";
const PREVIEW_RECIPIENT = "Alex Rivera";

// Fills the `{{placeholder}}` slots of a translated sentence with React nodes,
// so the sentence stays whole in the catalog while parts of it render styled.
const withNodes = (template, nodes) =>
  template.split(/(\{\{\s*\w+\s*\}\})/g).map((part, index) => {
    const match = part.match(/^\{\{\s*(\w+)\s*\}\}$/);
    if (match && nodes[match[1]] !== undefined) {
      return <React.Fragment key={index}>{nodes[match[1]]}</React.Fragment>;
    }
    return part;
  });

const SettingsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.is_superuser;

  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Points settings state
  const [pointsSettings, setPointsSettings] = useState({ min: 10, max: 100, step: 10, dollarRate: 1.0 });
  const [showPointsResetConfirm, setShowPointsResetConfirm] = useState(false);

  // Content moderation (profanity filter) state
  const [moderation, setModeration] = useState({ enabled: true, customWords: "" });

  // Post text: the connector between the two people named on a feed card.
  const [postText, setPostText] = useState({ recognition: "", nomination: "" });

  // Default UI language for members ("" = platform default / browser).
  const [defaultLocale, setDefaultLocale] = useState("");

  // Whether the platform has a translation engine configured (env-level).
  const { data: localeInfo } = useQuery({
    queryKey: ["i18n", "locale", "admin"],
    queryFn: i18nService.getMyLocale,
    staleTime: 60 * 1000,
  });
  const translationEnabled = !!localeInfo?.translation_enabled;

  // Superuser diagnostic: who the deployed AWS key is and what Translate says.
  const [checkResult, setCheckResult] = useState(null);
  const checkMutation = useMutation({
    mutationFn: () => i18nService.translationCheck(),
    onSuccess: (data) => setCheckResult(data),
    onError: (error) => setCheckResult({ error: formatError(error) }),
  });

  // Fetch organizations for the dropdown
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.getAllOrganizations(),
  });

  // Set org: use user's org for org admins, first org for super admins
  useEffect(() => {
    if (!selectedOrgId) {
      if (!isSuperAdmin && user?.organization_id) {
        setSelectedOrgId(user.organization_id);
      } else if (organizations.length > 0) {
        setSelectedOrgId(organizations[0].id);
      }
    }
  }, [organizations, selectedOrgId, isSuperAdmin, user?.organization_id]);

  // Load points settings when organization changes. Seed only once per org:
  // background refetches of ["organizations"] must not wipe unsaved edits.
  const seededOrgRef = useRef(null);
  useEffect(() => {
    if (selectedOrgId && organizations.length > 0 && seededOrgRef.current !== selectedOrgId) {
      const org = organizations.find((o) => o.id === selectedOrgId);
      if (org) {
        seededOrgRef.current = selectedOrgId;
        console.log("[SettingsSection] Loading org settings:", {
          orgId: org.id,
          orgName: org.name,
          min_recognition_points: org.min_recognition_points,
          max_recognition_points: org.max_recognition_points,
          points_step: org.points_step,
        });
        setPointsSettings({
          min: org.min_recognition_points ?? 10,
          max: org.max_recognition_points ?? 100,
          step: org.points_step ?? 10,
          dollarRate: org.points_to_dollar_rate ?? 1.0,
          allowGlobalPosting: org.allow_global_point_posting ?? false,
        });
        setModeration({
          enabled: org.profanity_filter_enabled ?? true,
          customWords: org.profanity_custom_words || "",
        });
        setPostText({
          recognition: org.recognition_phrase || "",
          nomination: org.nomination_phrase || "",
        });
        setDefaultLocale(org.default_locale || "");
      }
    }
  }, [selectedOrgId, organizations]);

  // Update points settings mutation
  const updatePointsMutation = useMutation({
    mutationFn: (settings) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("settings.errors.no_org_selected")));
      }
      const payload = {
        min_recognition_points: settings.min,
        max_recognition_points: settings.max,
        points_step: settings.step,
        points_to_dollar_rate: settings.dollarRate,
        allow_global_point_posting: settings.allowGlobalPosting,
      };
      console.log("[SettingsSection] Saving points settings:", payload, "for org:", selectedOrgId);
      return organizationsService.updateOrganization(selectedOrgId, payload);
    },
    onSuccess: (response) => {
      console.log("[SettingsSection] Save successful, response:", response);
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t("settings.toasts.points_saved"));
    },
    onError: (error) => {
      console.error("[SettingsSection] Save failed:", error);
      toast.error(formatError(error));
    },
  });

  // Save content-moderation (profanity filter) settings
  const updateModerationMutation = useMutation({
    mutationFn: (settings) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("settings.errors.no_org_selected")));
      }
      return organizationsService.updateOrganization(selectedOrgId, {
        profanity_filter_enabled: settings.enabled,
        profanity_custom_words: settings.customWords.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t("settings.toasts.moderation_saved"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Save the feed post-text connectors. Blank clears the override so the
  // default wording comes back.
  const updatePostTextMutation = useMutation({
    mutationFn: (text) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("settings.errors.no_org_selected")));
      }
      return organizationsService.updateOrganization(selectedOrgId, {
        recognition_phrase: text.recognition.trim() || null,
        nomination_phrase: text.nomination.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t("settings.toasts.post_text_saved"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Save the organization default language. Blank clears it so members fall
  // back to their browser language.
  const updateLocaleMutation = useMutation({
    mutationFn: (code) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error(t("settings.errors.no_org_selected")));
      }
      return organizationsService.updateOrganization(selectedOrgId, {
        default_locale: code || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["i18n", "locale"] });
      toast.success(t("settings.toasts.locale_saved"));
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Handle points settings change
  const handlePointsChange = (field, value) => {
    let newValue;
    if (field === 'dollarRate') {
      // Dollar rate: min 0.01, max 100, allow decimals
      newValue = Math.max(0.01, Math.min(100, parseFloat(value) || 0.01));
      // Round to 2 decimal places
      newValue = Math.round(newValue * 100) / 100;
    } else if (field === 'step') {
      newValue = Math.max(1, Math.min(100, value));
    } else {
      // No ceiling on min/max points — admins decide how high awards can go.
      // Minimum points may be 0 (recognitions without points); the max starts at 1.
      const minLimit = field === 'min' ? 0 : 1;
      newValue = Math.max(minLimit, value);
    }
    const newSettings = { ...pointsSettings, [field]: newValue };

    // Ensure min <= max
    if (field === 'min' && newValue > pointsSettings.max) {
      newSettings.max = newValue;
    }
    if (field === 'max' && newValue < pointsSettings.min) {
      newSettings.min = newValue;
    }

    setPointsSettings(newSettings);
  };

  // Save points settings
  const savePointsSettings = () => {
    if (pointsSettings.min > pointsSettings.max) {
      toast.error(t("settings.toasts.min_greater_than_max"));
      return;
    }
    updatePointsMutation.mutate(pointsSettings);
  };

  // Reset points to defaults (preserves the global-posting setting)
  const resetPointsToDefaults = () => {
    const reset = {
      min: 10,
      max: 100,
      step: 10,
      dollarRate: 1.0,
      allowGlobalPosting: pointsSettings.allowGlobalPosting ?? false,
    };
    setPointsSettings(reset);
    updatePointsMutation.mutate(reset);
    setShowPointsResetConfirm(false);
  };

  const pill = (value) => (
    <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium align-middle">
      {value}
    </span>
  );

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("settings.title")}</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">
          {t("settings.subtitle")}
        </p>
      </div>

      {/* Points Settings Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 flex-wrap">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
            <span className="truncate">{t("settings.points.title")}</span>
          </h2>
          {isSuperAdmin && (
            <select
              value={selectedOrgId || ""}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs sm:text-sm max-w-[160px] sm:max-w-none flex-shrink-0"
              disabled={isLoadingOrgs}
            >
              {isLoadingOrgs ? (
                <option>{t("settings.org_loading")}</option>
              ) : organizations.length === 0 ? (
                <option>{t("settings.no_organizations")}</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
          )}
        </div>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          {t("settings.points.description")}
        </p>

        <div className="space-y-6">
          {/* Min Points */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("settings.points.min_label")}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('min', pointsSettings.min - 10)}
                disabled={pointsSettings.min <= 0}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="0"
                value={pointsSettings.min}
                onChange={(e) => handlePointsChange('min', parseInt(e.target.value, 10) || 0)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('min', pointsSettings.min + 10)}
                disabled={pointsSettings.min >= pointsSettings.max}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">{t("settings.points.points_unit")}</span>
            </div>
          </div>

          {/* Max Points */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("settings.points.max_label")}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('max', pointsSettings.max - 10)}
                disabled={pointsSettings.max <= pointsSettings.min}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="1"
                value={pointsSettings.max}
                onChange={(e) => handlePointsChange('max', parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('max', pointsSettings.max + 10)}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">{t("settings.points.points_unit")}</span>
            </div>
          </div>

          {/* Step/Increments */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("settings.points.step_label")}
            </label>
            <p className="text-xs text-slate-500 mb-2">
              {t("settings.points.step_hint")}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('step', pointsSettings.step - 5)}
                disabled={pointsSettings.step <= 1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="1"
                max="100"
                value={pointsSettings.step}
                onChange={(e) => handlePointsChange('step', parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('step', pointsSettings.step + 5)}
                disabled={pointsSettings.step >= 100}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">{t("settings.points.step_unit")}</span>
            </div>
          </div>

          {/* Points to Dollar Rate */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("settings.points.rate_label")}
            </label>
            <p className="text-xs text-slate-500 mb-2">
              {t("settings.points.rate_hint")}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('dollarRate', Math.max(0.01, (pointsSettings.dollarRate || 1) - 0.1))}
                disabled={(pointsSettings.dollarRate || 1) <= 0.1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  min="0.01"
                  max="100"
                  step="0.01"
                  value={pointsSettings.dollarRate ?? 1.0}
                  onChange={(e) => handlePointsChange('dollarRate', parseFloat(e.target.value) || 0.01)}
                  className="w-28 pl-7 pr-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => handlePointsChange('dollarRate', Math.min(100, (pointsSettings.dollarRate || 1) + 0.1))}
                disabled={(pointsSettings.dollarRate || 1) >= 100}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">{t("settings.points.rate_unit")}</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {t("settings.points.rate_example", {
                amount: fmt.currency((pointsSettings.dollarRate ?? 1.0) * 100, "USD"),
              })}
            </p>
          </div>

          {/* Public point amounts */}
          <div>
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!pointsSettings.allowGlobalPosting}
                onChange={(e) =>
                  setPointsSettings({ ...pointsSettings, allowGlobalPosting: e.target.checked })
                }
                className="h-4 w-4 mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm font-medium text-slate-700">
                {t("settings.points.global_posting_label")}
              </span>
            </label>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 ml-6 sm:ml-7">
              {t("settings.points.global_posting_hint")}
            </p>
          </div>

          {/* Preview — plain inline text so the pills flow with the surrounding words instead of each wrapping to its own line. */}
          <div className="p-3 sm:p-4 bg-slate-50 rounded-2xl">
            <div className="text-xs sm:text-sm text-slate-600 mb-1.5 sm:mb-2">{t("settings.points.preview_label")}</div>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              {withNodes(t("settings.points.preview"), {
                min: pill(pointsSettings.min),
                max: pill(pointsSettings.max),
                step: pill(pointsSettings.step),
              })}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={savePointsSettings}
              disabled={updatePointsMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updatePointsMutation.isPending ? t("common:actions.saving") : t("common:actions.save_changes")}
            </button>
            <button
              onClick={() => setShowPointsResetConfirm(true)}
              disabled={updatePointsMutation.isPending}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors border border-slate-200 whitespace-nowrap"
            >
              <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {t("settings.points.reset_defaults")}
            </button>
          </div>
        </div>
      </div>

      {/* Post Text Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
          <span className="truncate">{t("settings.post_text.title")}</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          {t("settings.post_text.description")}
        </p>

        <div className="space-y-5">
          {[
            {
              key: "recognition",
              label: t("settings.post_text.recognition_label"),
              placeholder: DEFAULT_RECOGNITION_PHRASE,
            },
            {
              key: "nomination",
              label: t("settings.post_text.nomination_label"),
              placeholder: DEFAULT_NOMINATION_PHRASE,
            },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {label}
              </label>
              <input
                type="text"
                maxLength={60}
                value={postText[key]}
                onChange={(e) => setPostText({ ...postText, [key]: e.target.value })}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                {withNodes(
                  t("settings.post_text.preview", {
                    sender: PREVIEW_SENDER,
                    recipient: PREVIEW_RECIPIENT,
                  }),
                  {
                    phrase: (
                      <span className="font-medium text-slate-700">
                        {postText[key].trim() || placeholder}
                      </span>
                    ),
                  }
                )}
              </p>
            </div>
          ))}

          {/* Save */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={() => updatePostTextMutation.mutate(postText)}
              disabled={updatePostTextMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updatePostTextMutation.isPending ? t("common:actions.saving") : t("common:actions.save_changes")}
            </button>
          </div>
        </div>
      </div>

      {/* Language Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
          <span className="truncate">{t("settings.language.title")}</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          {t("settings.language.description")}
        </p>

        <div className="space-y-5">
          <div>
            <label
              htmlFor="org-default-locale"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              {t("settings.language.default_label")}
            </label>
            <LanguageSelect
              id="org-default-locale"
              value={defaultLocale}
              onChange={setDefaultLocale}
              inheritLabel={t("settings.language.inherit_option")}
            />
          </div>

          <div
            className={
              translationEnabled
                ? "text-xs rounded-2xl px-3 py-2 bg-green-50 border border-green-200 text-green-800"
                : "text-xs rounded-2xl px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800"
            }
          >
            {translationEnabled
              ? withNodes(t("settings.language.engine_on"), {
                  status: <strong>{t("settings.language.engine_on_status")}</strong>,
                })
              : withNodes(t("settings.language.engine_off"), {
                  status: <strong>{t("settings.language.engine_off_status")}</strong>,
                  provider: <code>TRANSLATION_PROVIDER=aws</code>,
                  permission: <code>translate:TranslateText</code>,
                })}
          </div>

          {isSuperAdmin && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => checkMutation.mutate()}
                disabled={checkMutation.isPending}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-full text-xs hover:bg-slate-50 disabled:opacity-50"
              >
                {checkMutation.isPending ? t("settings.language.checking") : t("settings.language.check_connection")}
              </button>
              {checkResult && (
                <pre className="text-[11px] leading-snug bg-slate-900 text-slate-100 rounded-2xl p-3 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(checkResult, null, 2)}
                </pre>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={() => updateLocaleMutation.mutate(defaultLocale)}
              disabled={updateLocaleMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updateLocaleMutation.isPending ? t("common:actions.saving") : t("common:actions.save_changes")}
            </button>
          </div>
        </div>
      </div>

      {/* Content Moderation Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
          <span className="truncate">{t("settings.moderation.title")}</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          {t("settings.moderation.description")}
        </p>

        <div className="space-y-5">
          {/* Enable toggle */}
          <div>
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!moderation.enabled}
                onChange={(e) =>
                  setModeration({ ...moderation, enabled: e.target.checked })
                }
                className="h-4 w-4 mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm font-medium text-slate-700">
                {t("settings.moderation.block_label")}
              </span>
            </label>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 ml-6 sm:ml-7">
              {t("settings.moderation.block_hint")}
            </p>
          </div>

          {/* Custom words */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t("settings.moderation.words_label")}
            </label>
            <p className="text-xs text-slate-500 mb-2">
              {t("settings.moderation.words_hint")}
            </p>
            <textarea
              value={moderation.customWords}
              onChange={(e) =>
                setModeration({ ...moderation, customWords: e.target.value })
              }
              disabled={!moderation.enabled}
              rows={3}
              placeholder={t("settings.moderation.words_placeholder")}
              className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Save */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={() => updateModerationMutation.mutate(moderation)}
              disabled={updateModerationMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updateModerationMutation.isPending ? t("common:actions.saving") : t("common:actions.save_changes")}
            </button>
          </div>
        </div>
      </div>

      {/* Recognition Logs Card */}
      <RecognitionLogsCard />

      {/* Points Reset Confirmation Modal */}
      {showPointsResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <RotateCcw className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{t("settings.reset_modal.title")}</h3>
            </div>
            <p className="text-slate-600 mb-6">
              {t("settings.reset_modal.body")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPointsResetConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
              >
                {t("common:actions.cancel")}
              </button>
              <button
                onClick={resetPointsToDefaults}
                disabled={updatePointsMutation.isPending}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {updatePointsMutation.isPending ? t("settings.reset_modal.resetting") : t("settings.reset_modal.reset")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsSection;
